import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import {
  eventTravelKey,
  formatDateTime,
  type EventTravelResponse,
  type RoomEntry,
} from "./travel"

// Lodging / accommodation data layer for the Console. Reuses the admin
// /api/v1/admin/event-travel read shape (the `rooms` array) — the same endpoint
// TravelPage uses — and flattens reservations into bookable rows for the
// CollectionView + a reading-pane detail. Writes go through the travel
// feature's staff lodging actions (adminAddTravelLodging /
// adminUpdateTravelLodging / adminDeleteTravelLodging — MCP-callable as
// travel__adminAddTravelLodging etc.); the console only calls them.

export const lodgingKey = (eventId: string) => eventTravelKey(eventId)

// Share the travel cache key so lodging + travel hit one round-trip.
export function lodgingQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: lodgingKey(eventId),
    queryFn: () =>
      api<EventTravelResponse>("/api/v1/admin/event-travel", {
        method: "POST",
        body: { eventId },
      }),
  })
}

export type LodgingOccupant = {
  id: string
  name: string
  email?: string | null
}

export type LodgingStatus = "upcoming" | "checked_in" | "checked_out"

export type LodgingDetailRow = { key: string; label: string; value: string }

export type LodgingBooking = {
  id: string
  hotelName: string
  location: string | null
  checkInAt: string | null
  checkOutAt: string | null
  nights: number | null
  roomType: string | null
  bedConfiguration: string | null
  occupancyLabel: string | null
  confirmation: string | null
  status: LodgingStatus
  occupants: LodgingOccupant[]
  detailRows: LodgingDetailRow[]
  /** The raw reservation row — prefills the edit form. */
  reservation: RoomEntry["reservation"]
}

export function buildLodging(data: EventTravelResponse | undefined): LodgingBooking[] {
  if (!data?.success) return []
  return (data.rooms ?? []).map(toBooking).sort(compareLodging)
}

function toBooking(entry: RoomEntry): LodgingBooking {
  const r = entry.reservation
  const nights = nightsBetween(r.checkInAt, r.checkOutAt)
  const location = [r.addressLocality, r.addressRegion].filter(Boolean).join(", ") || null
  const occupancyLabel = occupancy(r.numAdults, r.numChildren)
  return {
    id: r.id,
    hotelName: r.hotelName,
    location,
    checkInAt: r.checkInAt,
    checkOutAt: r.checkOutAt,
    nights,
    roomType: r.roomType,
    bedConfiguration: r.bedConfiguration,
    occupancyLabel,
    confirmation: r.confirmationNumber,
    status: deriveStatus(r.checkInAt, r.checkOutAt),
    reservation: r,
    occupants: entry.occupants.map((o) => ({
      id: o.guest.id,
      name: o.guest.name,
      email: o.guest.email,
    })),
    detailRows: [
      ["Hotel", r.hotelName],
      ["Location", location],
      ["Room type", r.roomType],
      ["Bed", r.bedConfiguration],
      ["Occupancy", occupancyLabel],
      ["Confirmation", r.confirmationNumber],
      ["Address", [r.streetAddress, r.postalCode, r.addressCountry].filter(Boolean).join(", ") || null],
      ["Requests", r.specialRequests ?? null],
      nights != null ? ["Nights", String(nights)] : null,
    ]
      .filter((row): row is [string, string] => Array.isArray(row) && !!row[1])
      .map(([key, value]) => ({ key, label: key, value })),
  }
}

export function compareLodging(a: LodgingBooking, b: LodgingBooking): number {
  const t = (a.checkInAt ?? "9999").localeCompare(b.checkInAt ?? "9999")
  if (t !== 0) return t
  return a.hotelName.localeCompare(b.hotelName)
}

export function occupantsLabel(occupants: LodgingOccupant[]): string {
  if (occupants.length === 0) return "No guests"
  if (occupants.length === 1) return occupants[0].name
  return `${occupants[0].name} +${occupants.length - 1}`
}

const STATUS_LABEL: Record<LodgingStatus, string> = {
  upcoming: "Upcoming",
  checked_in: "Checked-in",
  checked_out: "Checked-out",
}
export function lodgingStatusLabel(status: LodgingStatus): string {
  return STATUS_LABEL[status]
}

// Status from check-in / check-out windows (no server-side stay status yet).
function deriveStatus(checkInAt: string | null, checkOutAt: string | null): LodgingStatus {
  const now = Date.now()
  const inT = parseNaive(checkInAt)
  const outT = parseNaive(checkOutAt)
  if (outT != null && outT < now) return "checked_out"
  if (inT != null && inT <= now) return "checked_in"
  return "upcoming"
}

function parseNaive(value: string | null): number | null {
  if (!value) return null
  const t = Date.parse(value.includes("T") ? value : value.replace(" ", "T"))
  return Number.isNaN(t) ? null : t
}

function nightsBetween(a: string | null, b: string | null): number | null {
  if (!a || !b) return null
  const ad = new Date(a)
  const bd = new Date(b)
  if (Number.isNaN(ad.getTime()) || Number.isNaN(bd.getTime())) return null
  return Math.max(0, Math.round((bd.getTime() - ad.getTime()) / (24 * 3600_000)))
}

function occupancy(adults: number | null, children: number | null): string | null {
  const parts: string[] = []
  if (adults != null && adults > 0) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`)
  if (children != null && children > 0) parts.push(`${children} child${children === 1 ? "" : "ren"}`)
  return parts.length ? parts.join(", ") : null
}

// Re-export the shared date formatter so the lodging surface doesn't reach back
// into core/travel for it.
export { formatDateTime }

// ── writes (travel feature staff actions) ────────────────────────────────────

export type LodgingInput = {
  hotelName: string
  confirmationNumber?: string
  streetAddress?: string
  addressLocality?: string
  addressRegion?: string
  postalCode?: string
  addressCountry?: string
  /** Naive local `YYYY-MM-DDTHH:mm` (local-time-only convention). */
  checkInAt: string
  checkOutAt: string
  roomType?: string
  bedConfiguration?: string
  numAdults?: number
  numChildren?: number
  specialRequests?: string
}

type LodgingWriteResult = { success: boolean } & Record<string, unknown>

function cleanBody<T extends Record<string, unknown>>(obj: T): T {
  const out = {} as Record<string, unknown>
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue
    if (typeof v === "string" && v.trim() === "") continue
    out[k] = typeof v === "string" ? v.trim() : v
  }
  return out as T
}

/** One room shared by 1–10 guests (each must have been invited — the action
 *  409s GUEST_USER_MISSING otherwise). */
export function addLodging(input: LodgingInput & { guestIds: string[] }) {
  return api<LodgingWriteResult>("/api/v1/admin/travel-add-lodging", {
    method: "POST",
    body: cleanBody(input),
  })
}

/** Full replace of the room's details; occupants aren't editable here (same
 *  rule as travel movements). Omitted optional fields are cleared. */
export function updateLodging(reservationId: string, input: LodgingInput) {
  return api<LodgingWriteResult>("/api/v1/admin/travel-update-lodging", {
    method: "POST",
    body: cleanBody({ reservationId, ...input }),
  })
}

export function deleteLodging(reservationId: string) {
  return api<LodgingWriteResult>("/api/v1/admin/travel-delete-lodging", {
    method: "POST",
    body: { reservationId },
  })
}
